import tw, { theme } from 'twin.macro';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  AtSymbolIcon,
  ChatBubbleLeftRightIcon,
  ClipboardIcon,
  DevicePhoneMobileIcon,
  FlagIcon,
  MapPinIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import {
  BaseModal,
  IconButton,
  useCandidateLastChangeBadgeInfo,
  Title,
  IconComponentType,
  SectionAccordion,
  Alert,
} from '@restworld/ui-ds';
import { Badge, Button } from '@restworld/ui-ds-base';
import {
  imageVariant,
  useResponsiveBreakPoints,
  formatDateToDayMonthYear,
  useAnalytics,
  WhatsappIcon,
} from '@restworld/utils-common';
import { JobApplicationStatusKey } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';

import CandidatePresentationNote from './candidate-presentation-note';
import {
  candidateModalVariantAtom,
  CandidateModalVariantType,
  isCandidateHiredSuccessfullyAtom,
  selectedJobApplicationAtom,
  useCandidateStateChangeModal,
  isCandidateModalSuccessScreenAtom,
} from '../../../employer-job-listing.atoms';
import CandidateStatusChangeDropdown from './candidate-status-change-dropdown';
import CandidateProfileSection from './candidate-profile-section';
import CandidateModalHeader from './candidate-modal-header';
import CandidateInterview from './candidate-modal-interview-screen';
import { useDiscardCandidate } from './discard-candidate/use-discard-candidate';
import { CandidateStateChangeConfirmation } from './discard-candidate/candidate-state-change-confirmation';
import { HideCandidateConfirmation } from './discard-candidate/hide-candidate-confirmation';
import {
  BodyWrapper,
  CandidateNameWrapper,
  HeaderContentWrapper,
  HeaderWrapper,
  MainContentAndFooterWrapper,
  MainContentFooterWrapper,
  MainContentWrapper,
  MainWrapper,
  ModalHeaderContentWrapper,
  SideContentWrapper,
  BadgesWrapper,
} from './candidate-modal.styled';
import CandidateNotesSection from './candidate-notes-section';
import useUpdateEmployerViewedCandidateStatus from '../../../../spontaneous-candidates/hooks/use-update-employer-viewed-candidate-status';
import dynamic from 'next/dynamic';
import CandidateReferencesSection from './candidate-references-section';

// Import PdfViewer with SSR disabled to avoid DOMMatrix error
const PdfViewer = dynamic(() => import('../../../../pdf-viewer'), { ssr: false });

export function CandidateModal() {
  const { t } = useTranslation('common');
  const { showCandidateStateChangeModal, closeCandidateModal } = useCandidateStateChangeModal();
  const [variant] = useAtom(candidateModalVariantAtom);
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const [isCandidateHiredSuccessfully] = useAtom(isCandidateHiredSuccessfullyAtom);
  const [isCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);

  const { restaurantId, statusKey, workerId, jobPositionId, showDocumentCv } = useMemo(() => {
    const {
      worker: { user_id: workerId, show_document_cv: showDocumentCv } = { user_id: '', show_document_cv: false },
      restaurant,
      job_position_id,
      status_key: statusKey = '' as JobApplicationStatusKey,
    } = selectedJobApplication || {};
    const restaurantId = restaurant?.id || '';
    return { workerId, restaurantId, statusKey, jobPositionId: job_position_id, showDocumentCv };
  }, [selectedJobApplication]);
  const { isSpontaneousApplication } = useIsSpontaneousApplication();

  const isVariantWithSidePanel = useIsVariantWithSidePanel(variant);
  const modalHeader = useModalHeader();
  const modalVariant = useModalVariant();
  const candidate = useCurrentCandidate();
  const isDefaultView = useMemo(
    () => variant === 'default' || variant === 'worker_jp_discarded' || variant === 'references',
    [variant]
  );

  useUpdateEmployerViewedCandidateStatus({ statusKey, variant, restaurantId, workerId, jobPositionId });

  const showFullHeightContent = [
    'state_change_confirmation',
    'worker_jp_recovery_confirmation',
    'worker_jp_interview_scheduled_confirmation',
    'hide_candidate_confirmation',
  ].includes(variant);

  return (
    <BaseModal
      open={showCandidateStateChangeModal}
      onClose={closeCandidateModal}
      modalStyles={[
        tw`md:(w-full px-0 py-0 max-w-[85%] max-h-[95%] h-[95vh]) xl:(max-w-[65%])`,
        isCandidateHiredSuccessfully ? tw`bg-purple-50` : tw``,
      ]}
      noPadding
      hideHeader
      footer={
        modalVariant.footer ? (
          modalVariant.footer
        ) : isDefaultView || variant === 'spontaneous_candidate' ? (
          <div tw="flex items-center justify-between w-full px-4 lg:(px-10 pt-4)">
            <span tw="hidden text-base font-medium text-[#000000] lg:(block relative -top-2 flex-1)">
              {isSpontaneousApplication ? '' : t('take_an_action_on_this_application')}
            </span>
            <CandidateStatusChangeDropdown />
          </div>
        ) : null
      }
      footerStyles={[tw`shadow-[0px_-1px_16px_0px_rgba(0,0,26,0.25)]`]}
      title={<div tw="lg:hidden border-neutral-200">{isCandidateModalSuccessScreen ? null : modalHeader}</div>}
      hasHeaderBorderBottom={!isCandidateModalSuccessScreen}
    >
      <MainWrapper fullheight={showFullHeightContent}>
        {!isCandidateModalSuccessScreen && (
          <HeaderWrapper>
            <HeaderContentWrapper>{modalHeader}</HeaderContentWrapper>
            <IconButton ariaLabel={t('close')} Icon={XMarkIcon} onClick={closeCandidateModal} />
          </HeaderWrapper>
        )}
        <BodyWrapper fullheight={showFullHeightContent}>
          <MainContentAndFooterWrapper fullheight={showFullHeightContent} isDefaultView={isDefaultView}>
            {isVariantWithSidePanel ? (
              <>
                <MainContentWrapper fullheight={showFullHeightContent}>{modalVariant.body}</MainContentWrapper>
                <MainContentFooterWrapper>
                  {variant === 'references' ? (
                    <CandidateReferencesSection />
                  ) : ['notes', 'spontaneous_candidate_notes'].includes(variant) ? (
                    <CandidateNotesSection />
                  ) : showDocumentCv && candidate?.cvLink ? (
                    <SectionAccordion
                      title="CV"
                      TitleIcon={ClipboardIcon}
                      titleStyle={tw`md:text-lg`}
                      toggleBtn={{
                        wrapperStyle: tw`pb-4 mt-8`,
                      }}
                    >
                      <PdfViewer pdfUrl={candidate.cvLink} />
                    </SectionAccordion>
                  ) : (
                    <SectionAccordion
                      title="CV"
                      TitleIcon={ClipboardIcon}
                      titleStyle={tw`md:text-lg`}
                      toggleBtn={{
                        wrapperStyle: tw`pb-4 mt-8`,
                      }}
                    >
                      <CandidateProfileSection />
                    </SectionAccordion>
                  )}
                </MainContentFooterWrapper>
              </>
            ) : (
              <MainContentWrapper fullheight={showFullHeightContent}>{modalVariant.body}</MainContentWrapper>
            )}
          </MainContentAndFooterWrapper>
        </BodyWrapper>
      </MainWrapper>
    </BaseModal>
  );
}

export default CandidateModal;

// Custom Hooks

export const useModalHeader = () => {
  const { isDesktop } = useResponsiveBreakPoints();
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const [candidateModalVariant] = useAtom(candidateModalVariantAtom);

  return useMemo(() => {
    if (
      candidateModalVariant === 'worker_jp_interview_scheduled_confirmation' ||
      candidateModalVariant === 'worker_jp_discarded_confirmation'
    ) {
      return (
        <Title textColor="neutral-700" fontWeight="semibold" fontSize={isDesktop ? 'large' : 'base'} maxChars={10}>
          {`${selectedJobApplication?.worker?.user?.name} ${selectedJobApplication?.worker?.user?.surname}`}
        </Title>
      );
    }

    const showBadge =
      candidateModalVariant === 'default' ||
      candidateModalVariant === 'notes' ||
      candidateModalVariant === 'references';

    return (
      <ModalHeaderContentWrapper>
        <CandidateNameWrapper>
          <Title
            content={`${selectedJobApplication?.worker?.user?.name} ${selectedJobApplication?.worker?.user?.surname}`}
            textColor="neutral-700"
            fontWeight="semibold"
            fontSize={isDesktop ? 'large' : 'base'}
            maxChars={20}
          />
        </CandidateNameWrapper>
        {showBadge && <BadgeContent />}
      </ModalHeaderContentWrapper>
    );
  }, [
    candidateModalVariant,
    isDesktop,
    selectedJobApplication?.worker?.user?.name,
    selectedJobApplication?.worker?.user?.surname,
  ]);
};

export const useCurrentCandidate = () => {
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);

  return useMemo(
    () => ({
      email: selectedJobApplication?.worker?.user?.email || '',
      phoneNumber: selectedJobApplication?.worker?.user?.phone_number || '',
      whatsappPhoneNumber: selectedJobApplication?.worker?.user?.whatsapp_phone_number || '',
      image: imageVariant(selectedJobApplication?.worker?.main_picture, 'md') || '',
      cvLink: selectedJobApplication?.worker?.cv || '',
      firstName: selectedJobApplication?.worker?.name,
      lastName: selectedJobApplication?.worker?.surname,
      workerId: selectedJobApplication?.worker?.user_id,
      distanceToVenue: selectedJobApplication?.worker_distance_to_restaurant,
    }),
    [
      selectedJobApplication?.worker?.cv,
      selectedJobApplication?.worker?.main_picture,
      selectedJobApplication?.worker?.name,
      selectedJobApplication?.worker?.surname,
      selectedJobApplication?.worker?.user?.email,
      selectedJobApplication?.worker?.user?.phone_number,
      selectedJobApplication?.worker?.user?.whatsapp_phone_number,
      selectedJobApplication?.worker?.user_id,
      selectedJobApplication?.worker_distance_to_restaurant,
    ]
  );
};

export const useModalSidebar = () => {
  const [candidateModalVariant] = useAtom(candidateModalVariantAtom);
  const isDefaultVariant = candidateModalVariant === 'default';
  const { isSpontaneousCandidate } = useIsSpontaneousCandidate();

  return useMemo(
    () =>
      isDefaultVariant || isSpontaneousCandidate ? (
        <div tw="mt-16 flex flex-col gap-16">
          <AssessmentSection />
          <HighlightsSection />
          <PointsOfAttentionSection />
        </div>
      ) : null,
    [isDefaultVariant, isSpontaneousCandidate]
  );
};

export function useIsSpontaneousCandidate() {
  const [candidateModalVariant] = useAtom(candidateModalVariantAtom);

  const isSpontaneousCandidate = useMemo(
    () => candidateModalVariant === 'spontaneous_candidate' || candidateModalVariant === 'spontaneous_candidate_notes',
    [candidateModalVariant]
  );

  return { isSpontaneousCandidate };
}

export const useModalVariant = () => {
  const { closeCandidateModal: onClose } = useCandidateStateChangeModal();
  const [variant] = useAtom(candidateModalVariantAtom);
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const jobPositionId = useRouter().query.id as string;
  const candidate = useCurrentCandidate();
  const modalSidebar = useModalSidebar();

  const defaultVariantEl = (
    <>
      <CandidateModalHeader
        image={candidate?.image}
        firstName={candidate?.firstName}
        lastName={candidate?.lastName}
        cvLink={candidate?.cvLink}
      />
      <CandidateContactSection />
      <ReferencesIndicator />
      <div>{modalSidebar}</div>
    </>
  );

  const interviewVariantEl = useMemo(
    () => (
      <CandidateInterview
        firstName={candidate?.firstName}
        jobPositionId={jobPositionId}
        workerId={candidate?.workerId}
        onClose={onClose}
      />
    ),
    [candidate?.firstName, candidate?.workerId, jobPositionId, onClose]
  );

  const discard = useDiscardCandidate({
    firstName: candidate?.firstName,
    jobPositionId,
    workerId: candidate?.workerId,
    jobApplication: selectedJobApplication,
    onClose,
  });

  if (variant === 'worker_jp_interview_scheduled_confirmation') return { body: interviewVariantEl, footer: null };
  if (variant === 'worker_jp_discarded_confirmation') return { body: discard.body, footer: discard.footer };
  if (variant === 'hide_candidate_confirmation')
    return {
      body: <HideCandidateConfirmation jobPositionId={jobPositionId} workerId={candidate?.workerId} />,
      footer: null,
    };
  if (['state_change_confirmation', 'worker_jp_recovery_confirmation'].includes(variant))
    return {
      body: <CandidateStateChangeConfirmation jobPositionId={jobPositionId} workerId={candidate?.workerId} />,
      footer: null,
    };
  return { body: defaultVariantEl, footer: null };
};

// Components

function BadgeContent() {
  const { t: translateCommon } = useTranslation('common');
  const { getCandidateLastChangeBadgeInfo } = useCandidateLastChangeBadgeInfo();
  const [candidateModalVariant] = useAtom(candidateModalVariantAtom);
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);

  const { badgeText, badgeVariant } = getCandidateLastChangeBadgeInfo(
    selectedJobApplication?.last_status_change_date,
    selectedJobApplication.status_key
  );

  if (candidateModalVariant === 'spontaneous_candidate' || candidateModalVariant === 'spontaneous_candidate_notes') {
    return (
      <BadgesWrapper>
        {selectedJobApplication?.hidden && <Badge variant="disabled">{translateCommon('hidden')}</Badge>}
        {selectedJobApplication?.status_key === 'worker_jp_in_shortlist' && (
          <Badge variant="warning" tw="capitalize">
            {translateCommon('new')}
          </Badge>
        )}
        <Badge variant="neutral">{`${translateCommon('applied_on')} ${formatDateToDayMonthYear(
          selectedJobApplication?.inserted_at
        )}`}</Badge>
      </BadgesWrapper>
    );
  }
  return (
    <Badge variant={badgeVariant} tw="w-fit">
      {badgeText}
    </Badge>
  );
}

function CandidateContactSection() {
  const { t } = useTranslation('common');
  const candidateModalVariant = useAtomValue(candidateModalVariantAtom);
  const candidate = useCurrentCandidate();
  const candidateDistanceToVenue = useMemo(
    () => (candidate?.distanceToVenue ? `${(candidate?.distanceToVenue / 1000).toFixed(1)} km` : ''),
    [candidate?.distanceToVenue]
  );

  const renderCandidateInfo = useCallback(
    ({ text, Icon }: { text: string; Icon: IconComponentType }) => (
      <span tw="flex items-center gap-1 text-neutral-200">
        <Icon className="h-4 w-4 min-h-[16px] min-w-[16px]" />
        <span tw="text-base font-medium break-all">{text}</span>
      </span>
    ),
    []
  );

  const contactViaPhone = useCallback(
    () => (window.location.href = `tel:${candidate.phoneNumber}`),
    [candidate.phoneNumber]
  );

  const whatsappNumber = candidate.whatsappPhoneNumber || candidate.phoneNumber;

  const contactViaWhatsapp = useCallback(
    () => window.open(`https://wa.me/${whatsappNumber}`, '_blank', 'noopener,noreferrer'),
    [whatsappNumber]
  );

  if (candidateModalVariant !== 'default' && candidateModalVariant !== 'spontaneous_candidate') return null;

  return (
    <div>
      <div tw="border border-neutral-40 w-full p-4 rounded-lg flex flex-col gap-6 lg:(flex-row items-center) my-4">
        {!!candidateDistanceToVenue &&
          renderCandidateInfo({
            Icon: MapPinIcon,
            text: t('lives_distance_away_from_your_venue', { distance: candidateDistanceToVenue }),
          })}
        {!!candidate?.phoneNumber && renderCandidateInfo({ Icon: DevicePhoneMobileIcon, text: candidate.phoneNumber })}
        {!!candidate?.whatsappPhoneNumber &&
          candidate.whatsappPhoneNumber !== candidate.phoneNumber &&
          renderCandidateInfo({ Icon: WhatsappIcon, text: `WhatsApp: ${candidate.whatsappPhoneNumber}` })}
        {!!candidate.email && renderCandidateInfo({ Icon: AtSymbolIcon, text: candidate.email })}
      </div>
      <div tw="flex items-center gap-4">
        <Button
          StartIcon={DevicePhoneMobileIcon}
          variant="secondary-neutral"
          twStyle={tw`flex-1`}
          onClick={contactViaPhone}
          disabled={!candidate?.phoneNumber}
        >
          {t('call')}
        </Button>
        <Button
          StartIcon={WhatsappIcon}
          variant="secondary-neutral"
          twStyle={tw`flex-1`}
          onClick={contactViaWhatsapp}
          disabled={!candidate?.phoneNumber}
        >
          Whatsapp
        </Button>
      </div>
    </div>
  );
}

function AssessmentSection() {
  const { t } = useTranslation('common');
  const { t: tJP } = useTranslation('job-positions-page');
  const { isSpontaneousCandidate } = useIsSpontaneousCandidate();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const candidateModalVariant = useAtomValue(candidateModalVariantAtom);
  const { isSpontaneousApplication } = useIsSpontaneousApplication();

  const noEvaluationAnalysis = useMemo(
    () => !selectedJobApplication?.evaluation?.evaluation_analysis,
    [selectedJobApplication?.evaluation?.evaluation_analysis]
  );
  const notSpontaneousVariant = useMemo(
    () => candidateModalVariant !== 'spontaneous_candidate' && candidateModalVariant !== 'default',
    [candidateModalVariant]
  );
  const noCoverLetter = useMemo(() => !selectedJobApplication?.cover_letter, [selectedJobApplication?.cover_letter]);

  // Preselection-rejected candidates (Non idonei tab) should always show this section,
  // even without an AI evaluation, so the fallback note (worker withdrew / no evaluation) is rendered.
  const isPreselectionRejected = useMemo(
    () =>
      !selectedJobApplication?.added_to_shortlist_at &&
      (selectedJobApplication?.status_key === 'worker_jp_discarded' ||
        selectedJobApplication?.status_key === 'worker_jp_not_interested'),
    [selectedJobApplication?.added_to_shortlist_at, selectedJobApplication?.status_key]
  );

  if (
    (!isSpontaneousApplication && noEvaluationAnalysis && !isPreselectionRejected) ||
    (isSpontaneousApplication && (notSpontaneousVariant || noCoverLetter))
  ) {
    return null;
  }

  return (
    <SectionAccordion
      title={t(
        isSpontaneousCandidate || isSpontaneousApplication
          ? 'the_candidate_introduces_themself'
          : 'candidate_assessment'
      )}
      TitleIcon={isSpontaneousCandidate || isSpontaneousApplication ? ChatBubbleLeftRightIcon : SparklesIcon}
      titleStyle={tw`md:text-lg`}
      toggleBtn={{
        wrapperStyle: tw`pb-4`,
      }}
    >
      <SideContentWrapper>
        <ConstraintAlerts />
        <CandidatePresentationNote />
      </SideContentWrapper>
    </SectionAccordion>
  );
}

function ConstraintAlerts() {
  const { t: tJP } = useTranslation('job-positions-page');
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const { isSpontaneousApplication } = useIsSpontaneousApplication();

  if (isSpontaneousApplication || selectedJobApplication?.recommended) return null;

  const distFailed = selectedJobApplication?.evaluation?.distance_constraint_failed;
  const preassMissing =
    selectedJobApplication?.evaluation?.preassessment_not_completed || !selectedJobApplication?.has_finished_assessment;
  const evaluationAnalysis = selectedJobApplication?.evaluation?.evaluation_analysis;
  const isPreselectionRejected =
    !selectedJobApplication?.added_to_shortlist_at &&
    (selectedJobApplication?.status_key === 'worker_jp_discarded' ||
      selectedJobApplication?.status_key === 'worker_jp_not_interested');
  const isWorkerWithdrawal =
    isPreselectionRejected && selectedJobApplication?.status_key === 'worker_jp_not_interested';

  const alertIcon = (
    <div tw="p-2">
      <InformationCircleIcon width={24} color={theme`colors.orange.700`} />
    </div>
  );

  // Preselection-rejected candidates (Non idonei tab): pick the alert based on
  // the reason the AI/system marked them as unsuitable. The grey evaluation box
  // is hidden in these cases unless a real analysis exists (see CandidatePresentationNote).
  if (isPreselectionRejected) {
    // Case 4 + 5: the worker withdrew before reaching the shortlist.
    if (isWorkerWithdrawal) {
      return (
        <Alert
          icon={alertIcon}
          textStyling={tw`p-2`}
          variant="warning"
          text={tJP('preselection_alert_worker_withdrew')}
        />
      );
    }
    // Cases 2 + 3: preassessment incomplete — AI scored on CV signal only.
    if (preassMissing) {
      return <Alert icon={alertIcon} textStyling={tw`p-2`} variant="warning" text={tJP('warning_no_preassessment')} />;
    }
    // Case 6: hard-rule rejection (no preassessment flag, no narrative analysis).
    if (!evaluationAnalysis) {
      return (
        <Alert
          icon={alertIcon}
          textStyling={tw`p-2`}
          variant="warning"
          text={tJP('preselection_alert_base_requirements')}
        />
      );
    }
    // Case 1: analysis present, preassessment completed — no alert, only the grey box.
    return null;
  }

  if (distFailed && preassMissing) {
    return (
      <Alert
        icon={alertIcon}
        textStyling={tw`p-2`}
        variant="warning"
        text={
          <span>
            {tJP('warning_distance_and_preassessment_intro')}
            <ul tw="list-disc pl-4 mt-1">
              <li>{tJP('warning_distance_and_preassessment_distance')}</li>
              <li>{tJP('warning_distance_and_preassessment_preassessment')}</li>
            </ul>
          </span>
        }
      />
    );
  }

  if (distFailed) {
    return (
      <Alert
        icon={alertIcon}
        textStyling={tw`p-2`}
        variant="warning"
        text={tJP('warning_distance_constraint_failed')}
      />
    );
  }

  if (preassMissing) {
    return <Alert icon={alertIcon} textStyling={tw`p-2`} variant="warning" text={tJP('warning_no_preassessment')} />;
  }

  return null;
}

function HighlightsSection() {
  const { t } = useTranslation('common');
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const highlights = useMemo(
    () => selectedJobApplication?.evaluation?.highlights || [],
    [selectedJobApplication?.evaluation?.highlights]
  );

  if (!highlights?.length) return null;

  return (
    <SectionAccordion
      title={t('highlights')}
      TitleIcon={FlagIcon}
      titleStyle={tw`md:text-lg`}
      toggleBtn={{
        wrapperStyle: tw`pb-4`,
      }}
    >
      <div tw="flex items-center gap-4 flex-wrap">
        {highlights.map((highlight) => (
          <Badge key={highlight} variant="success">
            {highlight}
          </Badge>
        ))}
      </div>
    </SectionAccordion>
  );
}

function PointsOfAttentionSection() {
  const { t } = useTranslation('common');
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);

  const validAlerts = useMemo(() => {
    const alerts = selectedJobApplication?.evaluation?.alerts;
    if (!alerts) return [];
    return Object.keys(alerts)
      .filter((key) => alerts[key])
      .filter((key) => key !== 'motivation_unclear');
  }, [selectedJobApplication?.evaluation?.alerts]);

  if (!validAlerts?.length) return null;

  return (
    <SectionAccordion
      title={t('points_of_attention')}
      TitleIcon={FlagIcon}
      titleStyle={tw`md:text-lg`}
      toggleBtn={{
        wrapperStyle: tw`pb-4`,
      }}
    >
      <div tw="flex items-center gap-4 flex-wrap">
        {validAlerts?.map((alert) => (
          <Badge key={alert} variant="warning">
            {t(alert)}
          </Badge>
        ))}
      </div>
    </SectionAccordion>
  );
}

function ReferencesIndicator() {
  const { t } = useTranslation();
  const [variant, setVariant] = useAtom(candidateModalVariantAtom);
  const { trackEvent } = useAnalytics();
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const { user } = useAuth();
  const isDefaultVariant = useMemo(() => variant === 'default', [variant]);

  const totalReferenceHits = useMemo(
    () => selectedJobApplication?.worker?.worker_references?.length || 0,
    [selectedJobApplication?.worker?.worker_references?.length]
  );

  if (!totalReferenceHits || (!isDefaultVariant && variant !== 'spontaneous_candidate')) return null;

  return (
    <div tw="rounded-lg bg-green-50 p-3 w-full flex flex-col gap-3 mt-4 md:(flex-row items-center justify-between)">
      <span tw="text-neutral-700 text-sm!">
        <strong>{t('common:new_with_emoji_key')}</strong> {t('common:candidate_with_references_key')}
      </span>
      <Button
        size="sm"
        variant="secondary-success"
        twStyle={tw`w-full md:w-fit`}
        onClick={() => {
          trackEvent('employer_view_candidate_references', {
            user_id: user?.id,
            job_application_id: selectedJobApplication.id,
          });
          setVariant('references');
        }}
      >
        {t('common:read_the_references')}
      </Button>
    </div>
  );
}

// Utility

function useIsVariantWithSidePanel(variant: CandidateModalVariantType) {
  return [
    'default',
    'worker_jp_discarded',
    'notes',
    'spontaneous_candidate',
    'spontaneous_candidate_notes',
    'references',
  ].includes(variant);
}

function useIsSpontaneousApplication() {
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const isSpontaneousApplication = useMemo(
    () => !selectedJobApplication?.job_position_id,
    [selectedJobApplication?.job_position_id]
  );

  return { isSpontaneousApplication };
}
